import cn from 'classnames';
import * as React from 'react';
import styles from './DropDown.module.scss';
import { CaretDown } from 'src/common/icons';
import { Input } from 'src/components';

export type SelectInputOption = {
  key: string | number;
  value: string | number | React.ReactNode;
  optionDisabled?: boolean;
};

type NewDropdownProps = {
  options: Array<SelectInputOption>;
  selectedKeys: any[];
  onClick: (targetKey: any) => void;
  placeholder?: string;
  menuRightOffset?: any;
  isSearchable?: boolean;
  onSearchChange?: (searchTerm: string) => void;
};

// Helper function to extract text from React nodes for searching
const getTextFromReactNode = (node: React.ReactNode): string => {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return node.toString();
  if (Array.isArray(node)) return node.map(getTextFromReactNode).join(' ');
  if (React.isValidElement(node) && node.props.children) {
    return getTextFromReactNode(node.props.children);
  }
  return '';
};

export const CheckUsageDropDown: React.FC<NewDropdownProps> = ({
  options = [],
  selectedKeys = [],
  onClick,
  placeholder = 'Select an option',
  menuRightOffset = '',
  isSearchable = true,
  onSearchChange,
}) => {
  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const ref = React.useRef<HTMLDivElement>(null);
  const toggleRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const [menuWidth, setMenuWidth] = React.useState<number | null>(null);

  React.useEffect(() => {
    const handleResize = () => {
      if (open && toggleRef.current && menuRef.current) {
        const toggleWidth = toggleRef.current.offsetWidth;
        const menuWidth = menuRef.current.scrollWidth;
        setMenuWidth(Math.max(toggleWidth, menuWidth));
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [open, options]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  const filteredOptions = isSearchable
    ? options
        .filter((option) => {
          if (String(option.key).toLowerCase().startsWith('all')) {
            return true;
          }
          const textValue = getTextFromReactNode(option.value);
          return textValue.toLowerCase().includes(searchTerm.toLowerCase());
        })
        .sort((a, b) => {
          const aKey = String(a.key).toLowerCase();
          const bKey = String(b.key).toLowerCase();

          if (aKey.startsWith('all') && !bKey.startsWith('all')) return -1;
          if (!aKey.startsWith('all') && bKey.startsWith('all')) return 1;

          const aValue = getTextFromReactNode(a.value).toLowerCase();
          const bValue = getTextFromReactNode(b.value).toLowerCase();
          const searchTermLower = searchTerm.toLowerCase();
          const aIndex = aValue.indexOf(searchTermLower);
          const bIndex = bValue.indexOf(searchTermLower);

          if (aIndex !== bIndex) {
            return aIndex - bIndex;
          }
          if (aValue.length !== bValue.length) {
            return aValue.length - bValue.length;
          }
          return aValue.localeCompare(bValue);
        })
    : options;

  const selectedOption = options.find((option) => selectedKeys.includes(option.key));

  return (
    <div className={styles.newDropdown} ref={ref} onKeyDown={handleKeyDown}>
      <button type="button" onClick={() => setOpen(!open)} ref={toggleRef} className={styles.dropdownToggle}>
        <span>{selectedOption ? selectedOption.value : placeholder}</span>
        <span className={styles.arrow}>{open ? <CaretDown /> : <CaretDown />}</span>
      </button>
      {open && (
        <div
          ref={menuRef}
          style={{
            width: menuWidth ? `${menuWidth}px` : 'auto',
            ...(menuRightOffset ? { left: menuRightOffset } : {}),
          }}
          className={cn(styles.dropdownMenu, { [styles.open]: open })}
        >
          {isSearchable && (
            <div style={{ padding: '8px' }}>
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(value) => {
                  setSearchTerm(value);
                  onSearchChange?.(value);
                }}
                variant="secondary"
              />
            </div>
          )}
          {filteredOptions.length > 0 ? (
            filteredOptions.map(({ key, value, optionDisabled }) => (
              <div
                key={key}
                className={cn(styles.dropdownItem, {
                  [styles.selected]: selectedKeys.includes(key),
                  [styles.disabled]: optionDisabled,
                })}
                onClick={() => {
                  if (!optionDisabled) {
                    onClick(key);
                    setOpen(false);
                  }
                }}
              >
                {value}
              </div>
            ))
          ) : (
            <div className={styles.dropdownItem}>No options available</div>
          )}
        </div>
      )}
    </div>
  );
};

export default CheckUsageDropDown;